
declare global {
  interface Window {
    mcd: any;
  }
}


const DISTANCE_MAX: number = process.env.NODE_ENV === 'development' ? 10 : 10;

// Interfaccia per il risultato della verifica distanza
export interface DistanceCheckResult {
  result: string;
  source: 'Bridge' | 'Browser' | 'none';
  distance?: number;
}

// Funzione per calcolare la distanza tra due coordinate geografiche (formula di Haversine)
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Raggio della Terra in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return distance; // Ritorna la distanza in km
};

const geolocationFromBrowser = (storeLat: number, storeLng: number): Promise<DistanceCheckResult> => {
  return new Promise((resolve) => {
    // Verifica che la geolocalizzazione sia disponibile nel browser
    if (!navigator.geolocation) {
      console.error('Geolocalizzazione non supportata dal browser');
      resolve({ result: 'LOCATION_NOT_SUPPORTED', source: 'Browser' });
      return;
    }

    // Ottieni la posizione corrente dell'utente
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLat = position.coords.latitude;
        const userLng = position.coords.longitude;
        
        console.log(`Posizione utente: ${userLat}, ${userLng}`);
        console.log(`Posizione store: ${storeLat}, ${storeLng}`);
        
        // Calcola la distanza tra utente e store
        const distance = calculateDistance(userLat, userLng, storeLat, storeLng);
        console.log(`Distanza calcolata: ${distance.toFixed(2)} km`);
        
        // Verifica se la distanza è valida
        if (distance <= DISTANCE_MAX) {
          console.log('Distanza valida');
          resolve({ result: 'DISTANCE_VALID', source: 'Browser', distance });
        } else {
          console.log('Distanza non valida (troppo lontano)');
          resolve({ result: 'DISTANCE_NOT_VALID', source: 'Browser', distance });
        }
      },
      (error) => {
        console.error('Errore nella geolocalizzazione:', error.message);
        resolve({ result: 'LOCATION_ERROR', source: 'Browser' });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000, // 10 secondi di timeout
        maximumAge: 0
      }
    );
  });
};

const getLocationFromBridge = (storeLat: number, storeLng: number): Promise<DistanceCheckResult> => {
  return new Promise((resolve) => {
    try {
      // Verifica che il bridge sia disponibile
      if (typeof window === 'undefined' || 
          typeof window.mcd === 'undefined' || 
          !window.mcd || 
          !window.mcd.bridge) {
        console.log('Bridge non disponibile, uso geolocalizzazione browser');
        // Fallback alla geolocalizzazione del browser
        geolocationFromBrowser(storeLat, storeLng).then(resolve);
        return;
      }

      const locationBridge = window.mcd.bridge.message('location');
      locationBridge.send({"continuous" : false});
      
      locationBridge.on('data', function(data: any){
        console.log("Location data dal bridge: " + JSON.stringify(data));
        const userLat = data.latitude;
        const userLng = data.longitude;
        
        // Calcola la distanza tra utente e store
        const distance = calculateDistance(userLat, userLng, storeLat, storeLng);
        console.log(`Distanza calcolata dal bridge: ${distance.toFixed(2)} km`);
        
        // Verifica se la distanza è valida
        if (distance <= DISTANCE_MAX) {
          console.log('Distanza valida (dal bridge)');
          resolve({ result: 'DISTANCE_VALID', source: 'Bridge', distance });
        } else {
          console.log('Distanza non valida (troppo lontano, dal bridge)');
          resolve({ result: 'DISTANCE_NOT_VALID', source: 'Bridge', distance });
        }
      });
      
      locationBridge.on('error', function(error: any){
        console.log("Errore location bridge: " + JSON.stringify(error));
        console.log('Fallback a geolocalizzazione browser');
        // Fallback alla geolocalizzazione del browser
        geolocationFromBrowser(storeLat, storeLng).then(resolve);
      });
      
      // Timeout di sicurezza: dopo 5 secondi passa al browser
      setTimeout(() => {
        console.log('Timeout bridge, uso geolocalizzazione browser');
        geolocationFromBrowser(storeLat, storeLng).then(resolve);
      }, 5000);
      
    } catch (err) {
      console.error('Errore nell\'avvio del bridge location:', err);
      // Fallback alla geolocalizzazione del browser
      geolocationFromBrowser(storeLat, storeLng).then(resolve);
    }
  });
};

export const storeService = {
  // Verifica la distanza tra l'utente e lo store
  checkDistance: async (siteId: number): Promise<DistanceCheckResult> => {
    
    console.log("===== checkDistance : " + siteId);
    try {
      // Recupera i dati dello store
      const response = await fetch('https://friends.c1-p8.com/store_platform.json');
      const data = await response.json();
      const site = data.sites.find((site: any) => parseInt(site.mcdid) === siteId) || null;
      
      if (site == null) {
        console.log('STORE_NOT_FOUND');
        return { result: 'STORE_NOT_FOUND', source: 'none' };
      }
      
      // Verifica che lo store abbia le coordinate
      if (!site.geo || !site.geo.lat || !site.geo.lng) {
        console.log('STORE_COORDINATES_NOT_FOUND');
        return { result: 'STORE_COORDINATES_NOT_FOUND', source: 'none' };
      }
      
      console.log(`Store trovato: ${site.name || site.mcdid}`);
      console.log(`Coordinate store: ${site.geo.lat}, ${site.geo.lng}`);
      
      // Prima prova con il bridge, poi fallback al browser
      const distanceResult = await getLocationFromBridge(site.geo.lat, site.geo.lng);
      return distanceResult;
      
    } catch (error: any) {
      console.error('Errore nel check distanza:', error.message);
      return { result: 'STORE_PLATFORM_NOT_FOUND', source: 'none' };
    }
  }
};